import { Component, ElementRef, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { SignatureAndLicenseService } from '../signature-widget/signature-and-license.service';

/**
 * License Widget
 */
@Component({
  selector: 'license-widget',
  templateUrl: './license-widget.component.html',
  styleUrls: ['./license-widget.component.scss', '../../common/css/general.scss']
})
export class LicenseWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * The current layout index
   */
  public currentLayoutIndex = 1;

  /**
   * Initial widget height
   */
  widgetHeight = 500;//default

  /**
   * Constructor
   * @param injector
   * @param signatureAndLicenseService
   * @param componentEl
   */
  constructor(injector: Injector, public signatureAndLicenseService: SignatureAndLicenseService, private componentEl: ElementRef) {
    super(injector, 'signatureAndLicense', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.executeWidgetDataLoadedInWidgetClass = true;
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler('signatureAndLicense', this.provideSaveObject.bind(this));
  }

  /**
   * Choose next available layout when adding text widget into report document
   */
  private chooseNextLayout() {
    const activeDocument = this.rdStorage.getActiveDocument();
    const usedLayouts: string[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId === this.widgetConfig.widgetId) {//just text widgets
          if (usedLayouts.indexOf(widget.widgetSettings.currentLayout) < 0) {
            usedLayouts.push(widget.widgetSettings.currentLayout);
          }
        }
      }
    }

    if (!this.widgetConfig.widgetSettings.availableLayouts) {
      this.widgetConfig.widgetSettings.availableLayouts = ["1", "2", "3", "4"];
    }
    for (const layout of this.widgetConfig.widgetSettings.availableLayouts) {
      if (usedLayouts.indexOf(layout) < 0) {
        this.currentLayout = layout;
        this.widgetConfig.widgetSettings.currentLayout = this.currentLayout;
        break;
      }
    }

    setTimeout(() => {
      this.eventBus.broadcastEvent('widgetsChanged');
    });
  }

  /**
   * The layout was changed
   * @param newLayout
   */
  public layoutChanged(newLayout: string): void {
    super.layoutChanged(newLayout);

    this.currentLayoutIndex = parseInt(newLayout) - 1;
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('signatureAndLicense').pipe(take(1));

    const reportId = this.mockup ? -1 : this.getReport();

    if (!this.currentLayout || this.currentLayout === "default" || this.currentLayout === "ChosenOnAdd") {
      this.chooseNextLayout();
    }

    this.signatureAndLicenseService.selectedData$.subscribe((data: any[]) => {
      if (data) {
        //setDirtyFlag was called even though nothing changed (e.g. loading report), so we need some double check
        const dataChanged = this.editMode || !this.widgetData || (JSON.stringify(this.widgetData) !== JSON.stringify(data));
        this.updateWidgetData(data);
        if (dataChanged) {
          this.setDirtyFlag(true, true);
        }
      }
    });

    this.currentLayoutIndex = parseInt(this.currentLayout) - 1;

    this.signatureAndLicenseService.init(reportId);

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('signatureAndLicense', this.getReport()).pipe();

    this.signatureAndLicenseService.widgetDataLoaded$.subscribe(data => {
      if (data) {
        this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
        this.setDirtyFlag(false);
      }
    })
  }

  /**
   * Widget data is ready
   */
  protected widgetDataReady(): void {
    this.signatureAndLicenseService.setInitialData(this.widgetData);
    setTimeout(() => this.widgetResized());
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Get selected licence
   */
  public getLicense() {
    const data = this.widgetData[this.currentLayoutIndex];
    if (data?.licenses?.length) {
      return data.licenses[0].pdf_url;
    }
    return 'N/A';
  }

  /**
   * widgetResized event handler
   */
  protected widgetResized() {
    setTimeout(() => {
      const el = this.componentEl.nativeElement;
      this.widgetHeight = el.closest('.gridster-item').offsetHeight;
    }, 10);
  }
}
